import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload } from 'lucide-react';
import { PrimaryButton } from 'src/shared/components/button/Button';
import Alert from 'src/shared/components/ui/alert';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from 'src/shared/components/ui/sheet';
import { RiskPortfolioViews } from 'src/routes/routesConfig';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { usePreviewSiteManifestMutation } from '../../state/api/organisationEngineSlice';

type SiteManifestUploadDrawerProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    currentCustomerId: string;
};

const GENERIC_ERROR = 'Could not preview the manifest. Please check the file and try again.';

// CSV-level failures (bad headers, empty body, server unreachable) come back
// from the engine as a non-200 — surface them inside the drawer so the user
// can fix and retry without leaving the site-management page. A successful
// preview navigates to the dedicated preview page, passing the result via
// route state so the user can inspect per-row classifications.
const SiteManifestUploadDrawer = ({ open, onOpenChange, currentCustomerId }: SiteManifestUploadDrawerProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [previewSiteManifest, { isLoading }] = usePreviewSiteManifestMutation();
    const navigate = useNavigate();
    // Captured inside the catch so the alert renders regardless of whether RTK
    // Query's hook-level `error` field has propagated yet.
    const [uploadError, setUploadError] = useState<string | null>(null);

    // Tracks the latest `open` prop so the async upload handler can check
    // whether the user cancelled mid-flight. Without this guard, a late
    // preview response would push the user to the preview page even after
    // they closed the drawer.
    const openRef = useRef(open);
    useEffect(() => {
        openRef.current = open;
    }, [open]);

    // Clear any stale error from a previous open whenever the drawer reopens.
    useEffect(() => {
        if (open) setUploadError(null);
    }, [open]);

    const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        const csv = await file.text();
        setUploadError(null);
        try {
            const preview = await previewSiteManifest({ bcId: currentCustomerId, csv }).unwrap();
            if (!openRef.current) return; // user cancelled mid-upload
            onOpenChange(false);
            navigate(navRoutesConfig[RiskPortfolioViews.SITE_MANIFEST_PREVIEW]({ customerId: currentCustomerId }), {
                state: { preview, csv },
            });
        } catch (err) {
            if (!openRef.current) return;
            setUploadError(extractErrorMessage(err));
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side='right' className='w-[420px] sm:max-w-[420px]' data-testid='manifest-upload-drawer'>
                <SheetHeader>
                    <SheetTitle>Import Site Manifest</SheetTitle>
                    <SheetDescription>
                        Upload a manifest CSV to preview what would change. Nothing is saved until you confirm on the next page.
                    </SheetDescription>
                </SheetHeader>

                <div className='mt-6 flex flex-col gap-4'>
                    <input
                        ref={fileInputRef}
                        type='file'
                        accept='.csv,text/csv'
                        className='hidden'
                        onChange={handleFileSelected}
                        data-testid='manifest-file-input'
                    />
                    <PrimaryButton onClick={() => fileInputRef.current?.click()} disabled={isLoading} data-testid='choose-manifest-button'>
                        <Upload className='w-4 h-4' /> {isLoading ? 'Uploading…' : 'Choose CSV file'}
                    </PrimaryButton>

                    {uploadError && (
                        <Alert severity='error' data-testid='manifest-upload-error'>
                            {uploadError}
                        </Alert>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

// The engine returns 400 with `{ error: "<message>" }` for header / parse
// problems. Fall back to a generic message if the shape is unexpected.
const extractErrorMessage = (err: unknown): string => {
    if (typeof err === 'object' && err !== null && 'data' in err) {
        const data = (err as { data: unknown }).data;
        if (typeof data === 'object' && data !== null && 'error' in data && typeof (data as { error: unknown }).error === 'string') {
            return (data as { error: string }).error;
        }
    }
    return GENERIC_ERROR;
};

export default SiteManifestUploadDrawer;
